var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create, destroy, EVENT, } from 'zoid/dist/zoid.frameworks.frame';
import { getContainerCss } from './components/loader/css';
import { loaderComponentHtml } from './components/loader/html';
import { getCSS } from './components/modal/css';
import { getModalWrapper } from './components/modal/html';
import { isRunningInCordova, openInCordovaInAppBrowser } from './utils/cordova';
import { openOauthUrlInCordovaBrowser } from './utils/oauth';
import { once } from './utils/once';
import { adaptPluggyConnectProps, } from './utils/props';
import { getSdkVersion } from './version';
// the URL where the widget component is being hosted
const CONNECT_PRODUCTION_URL = 'https://connect.pluggy.ai';
let zoidComponentInstance;
// reference to the container uid, generated once it's rendered
let pluggyConnectContainerUid;
/**
 * Helper to wrap container class names with current container UID
 * @param className
 */
function containerCssClassName(className) {
    return `${pluggyConnectContainerUid}_${className}`;
}
/**
 * Method to be executed from both the child and parent components, to execute the
 * handshake between both so the parent can render the child.
 *
 * @returns a singleton ZoidComponent reference, that will take care of
 * wrapping and rendering the actual ZoidComponentInstance (this
 * should be instantiated only once per page/context).
 */
export function initialize() {
    if (!zoidComponentInstance) {
        zoidComponentInstance = create({
            // The html tag used to render the component
            tag: 'pluggy-connect-widget',
            // The url of the page that will show in the iframe or popup, when someone includes the component on their site
            url: ({ props }) => {
                const { url: urlByProp = CONNECT_PRODUCTION_URL } = props;
                return urlByProp;
            },
            dimensions: {
                width: '320px',
                height: '568px',
            },
            props: {
                connectToken: {
                    type: 'string',
                },
                url: {
                    type: 'string',
                    required: false,
                },
                includeSandbox: {
                    type: 'boolean',
                    required: false,
                },
                allowConnectInBackground: {
                    type: 'boolean',
                    required: false,
                },
                allowFullscreen: {
                    type: 'boolean',
                    required: false,
                },
                updateItem: {
                    type: 'string',
                    required: false,
                },
                connectorTypes: {
                    type: 'array',
                    required: false,
                },
                connectorIds: {
                    type: 'array',
                    required: false,
                },
                countries: {
                    type: 'array',
                    required: false,
                },
                selectedConnectorId: {
                    type: 'number',
                    required: false,
                },
                language: {
                    type: 'string',
                    required: false,
                },
                theme: {
                    type: 'string',
                    required: false,
                },
                moveSecurityData: {
                    type: 'string',
                    required: false,
                },
                products: {
                    type: 'array',
                    required: false,
                },
                sdkVersion: {
                    type: 'array',
                    required: false,
                    default: () => getSdkVersion(),
                },
                _runningInCordova: {
                    type: 'boolean',
                    required: false,
                    default: () => isRunningInCordova(),
                },
                onSuccessProp: {
                    type: 'function',
                    required: false,
                },
                onErrorProp: {
                    type: 'function',
                    required: false,
                },
                onOpenProp: {
                    type: 'function',
                    required: false,
                },
                onHideProp: {
                    type: 'function',
                    required: false,
                },
                onCloseProp: {
                    type: 'function',
                    required: false,
                },
                onEventProp: {
                    type: 'function',
                    required: false,
                },
            },
            attributes: {
                iframe: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
                popup: {
                    scrolling: 'no',
                    title: 'Pluggy',
                },
            },
            prerenderTemplate({ doc, props }) {
                const htmlNew = doc.createElement('html');
                htmlNew.innerHTML = loaderComponentHtml({
                    nonce: props.cspNonce,
                });
                return htmlNew;
            },
            containerTemplate({ doc, dimensions: { height, width }, close, uid, frame, prerenderFrame, event, props, }) {
                if (!prerenderFrame || !frame) {
                    throw new Error('Unexpected state: prerenderFrame or frame not defined');
                }
                // set container uid global reference
                pluggyConnectContainerUid = uid;
                const container = doc.createElement('div');
                const connectTheme = props.theme || 'light';
                container.id = uid;
                container.innerHTML = getModalWrapper(uid, connectTheme);
                const frameContainer = container.querySelector(`.${uid}_container`);
                if (!frameContainer) {
                    throw new Error('Unexpected state: not found frame container');
                }
                const visibleClassName = containerCssClassName('visible');
                const invisibleClassName = containerCssClassName('invisible');
                const prerenderFrameClassName = containerCssClassName('prerender-frame');
                frameContainer.appendChild(frame);
                frameContainer.appendChild(prerenderFrame);
                // Add styles
                const style = doc.createElement('style');
                // if allowFullscreen is not defined, default to true
                const allowFullscreenOrDefault = props.allowFullscreen !== undefined ? props.allowFullscreen : true;
                style.innerHTML = [
                    getCSS({
                        uid,
                        themeColor: 'fafafa',
                        height,
                        width,
                        allowFullscreen: allowFullscreenOrDefault,
                    }),
                    getContainerCss({ uid }),
                ].join('\n');
                container.appendChild(style);
                prerenderFrame.classList.add(prerenderFrameClassName);
                prerenderFrame.classList.add(visibleClassName);
                frame.classList.add(invisibleClassName);
                event.on(EVENT.RENDERED, () => {
                    // hide prerender frame, display content frame
                    prerenderFrame.classList.remove(visibleClassName);
                    prerenderFrame.classList.add(invisibleClassName);
                    frame.classList.remove(invisibleClassName);
                    frame.classList.add(visibleClassName);
                });
                // Remove scrolling from document body when modal is open (ie. visible)
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                event.on(EVENT.DISPLAY, () => {
                    // modal displayed, add visible class & call onOpen callback
                    document.body.classList.add(modalVisibleClassName);
                    const { onOpenProp } = props;
                    onOpenProp === null || onOpenProp === void 0 ? void 0 : onOpenProp();
                });
                // Wrap onCloseProp to be called only once, per component instance, which is
                // what we want to allow.
                // This "hack" is necessary because Zoid may trigger 2 times EVENT.CLOSE event,
                // for example when removing the container element from DOM and when explicitly
                // calling close() handler at the same time.
                // Each time a new container/component is rendered, the props are re-created from scratch,
                // so this will work for all cases.
                const onClosePropWrapper = props.onCloseProp
                    ? once(props.onCloseProp)
                    : null;
                event.on(EVENT.CLOSE, () => {
                    // modal closed, remove visible class & call onClose callback
                    // Note: this callback may be called 2 times in some cases due to Zoid logic,
                    // for example when unmounting container component AND calling close() explicitly.
                    document.body.classList.remove(modalVisibleClassName);
                    onClosePropWrapper === null || onClosePropWrapper === void 0 ? void 0 : onClosePropWrapper();
                });
                // receive message from iframe
                window.addEventListener('message', (event) => {
                    const { origin, data } = event;
                    const { url: connectWebappUrl = 'https://connect.pluggy.ai', } = props;
                    if (origin !== connectWebappUrl) {
                        // origin not matching, comming from another place -> ignore message
                        return;
                    }
                    let pluggyConnectMessage;
                    try {
                        pluggyConnectMessage = JSON.parse(data);
                    }
                    catch (_a) {
                        // could not parse, data is not a valid JSON string
                        return;
                    }
                    if (pluggyConnectMessage.type === 'OAUTH_OPEN' &&
                        isRunningInCordova()) {
                        const { message: oauthUrl } = pluggyConnectMessage;
                        // in the case of pluggy-connect running in Cordova mobile native environment
                        // we must handle the Oauth popup opening manually by explicitly managing a new iframe window
                        openOauthUrlInCordovaBrowser(oauthUrl, (payload) => { var _a; return (_a = frame.contentWindow) === null || _a === void 0 ? void 0 : _a.postMessage(payload, '*'); });
                        return;
                    }
                    if (pluggyConnectMessage.type === 'LINK_OPEN' &&
                        isRunningInCordova()) {
                        const { message: externalUrl } = pluggyConnectMessage;
                        // in the case of pluggy-connect running in Cordova mobile native environment,
                        // open the link specifically in the system browser,
                        // so the main app frame is not blocked, and the user can go back to the app easily
                        openInCordovaInAppBrowser(externalUrl, '_system');
                        return;
                    }
                    if (pluggyConnectMessage.type === 'CONTINUE_IN_BACKGROUND') {
                        // hide pluggy-connect, we remove the 'has-modal-visible' class to restore the scroll
                        document.body.classList.remove(modalVisibleClassName);
                        return;
                    }
                }, false);
                // Register modal close() on 'Escape' key button press
                document.addEventListener('keydown', function escapeKeyCloseHandler(event) {
                    if (event.key !== 'Escape') {
                        return;
                    }
                    // escape key -> close (and remove 'keydown' listener)
                    close();
                    document.removeEventListener('keydown', escapeKeyCloseHandler);
                });
                return container;
            },
        });
    }
    return zoidComponentInstance;
}
export class PluggyConnect {
    constructor(props) {
        this.zoidComponent = initialize();
        // extend component props with current wrapper props
        const extendedProps = Object.assign(Object.assign({}, props), { sdkVersion: getSdkVersion() });
        // adapt props to avoid collision with Zoid component props
        // assign to private variable for reusability by React wrapper
        this.componentPropsExtendedAdapted = adaptPluggyConnectProps(extendedProps);
    }
    /**
     * Render the component using the specified component props,
     * as a modal with an iframe, appended to the page body (or the DOM uppermost element).
     *
     * @param containerElement - parent element where component should be rendered at. If not specified, will render at body root element.
     * @returns promise that resolves when rendered successfully, or throws if failed.
     */
    init(containerElement) {
        const container = containerElement ||
            (document.getElementsByTagName('body') ||
                document.getElementsByTagName('html') ||
                document.getElementsByTagName('div'))[0];
        // initialize zoid componentInstance
        this.componentInstance = this.zoidComponent(this.componentPropsExtendedAdapted);
        return this.componentInstance.render(container).catch((error) => {
            console.error(`Failed to render <PluggyConnect /> component`, error);
            throw error;
        });
    }
    /**
     * Manually cleanup the component.
     * This is not recommended, you should create the component once
     * and re-render it as needed each new time to save resources.
     */
    destroy() {
        zoidComponentInstance = undefined;
        return destroy();
    }
    /**
     * If the component is minimized, re-open it.
     * @retruns promise that resolves when re-opened successfully, or throws if failed.
     */
    show() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.componentInstance) {
                throw new Error('Failed to show <PluggyConnect /> component: component not initialized');
            }
            try {
                // open wrapper modal
                yield this.componentInstance.show();
                // add modal visible class
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                document.body.classList.add(modalVisibleClassName);
            }
            catch (error) {
                console.error(`Failed to show <PluggyConnect /> component`, error);
                throw error;
            }
        });
    }
    /**
     * Minimize the component.
     * Useful if you want to hide Connect widget after credentials have been submitted,
     * and you want to just continue connecting in background while still listening to
     * other callback events.
     */
    hide() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.componentInstance) {
                throw new Error('Failed to hide <PluggyConnect /> component: component not initialized');
            }
            try {
                // hide wrapper modal
                yield this.componentInstance.hide();
                // remove modal visible class
                const modalVisibleClassName = containerCssClassName('has-modal-visible');
                document.body.classList.remove(modalVisibleClassName);
            }
            catch (error) {
                console.error(`Failed to hide <PluggyConnect /> component`, error);
                throw error;
            }
        });
    }
}
export default PluggyConnect;
//# sourceMappingURL=data:application/json;base64,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